import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy, Renderer2, SimpleChanges } from '@angular/core';
import { AppUtils } from 'src/app/shared/app.utils';
import { AddressReverseGeocode, AddressLocation } from 'src/app/shared/models/location-models';
import { Router, ActivatedRoute } from '@angular/router';
import Graphic from "@arcgis/core/Graphic";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Point from '@arcgis/core/geometry/Point';
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

// configure esri-loader to use version from a locally hosted build of the API
// NOTE: make sure this is called once before any calls to loadModules()
//setDefaultOptions({ url: environment.arcGISAPIUrl });

@Component({
  selector: 'ness-crime-gun-map',
  templateUrl: './crime-gun-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe]
})
export class CrimeGunMapComponent {
  private _gunEvents: ServerModels.GunDetailEventData[] = [];
  get gunEvents(): ServerModels.GunDetailEventData[] {
    return this._gunEvents;
  }
  @Input() set gunEvents(events: ServerModels.GunDetailEventData[]) {
    this._gunEvents = events;
  }

  @Output() mapLoadedEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() pinMovedEvent = new EventEmitter();

  @ViewChild('mapViewNode', { static: true }) private mapViewEl: ElementRef;

  /**
   * _zoom sets map zoom
   * _center sets map center
   * _basemap sets type of map
   * _loaded provides map loaded status
   */
  private _addresses: any[] = [];
  private _basemap = 'streets';
  private _center: Array<number> = [-77.004657, 38.908030];
  private _zoom = 10;

  private _pinnedLocation: AddressReverseGeocode;
  private _isPrimaryMap: boolean;
  private _wkid = 4326;
  private isDragging = false;

  private _mapView = null;

  @Input() set basemap(basemap: string) {
    this._basemap = basemap;
  }

  @Input() set center(center: any) {
    this._center = center;
  }

  @Input() set zoom(zoom: number) {
    this._zoom = zoom;
  }

  @Input() set pinnedLocation(location: AddressReverseGeocode) {
    this._pinnedLocation = location;
  }

  @Input() set isPrimaryMap(value: boolean) {
    this._isPrimaryMap = value;
  }

  locationData: AddressLocation;
  consolidatedAddress: string;
  latitudeRegEx: RegExp = /^[-]?([1-8]?\d(?:\.[0-9]{1,80})?|90(\.0+))?$/;
  longitudeRegEx: RegExp = /^[-]?(180(\.0{1,80})?|((1[0-7]\d)|([1-9]?\d))(\.\d{1,80}))?$/;

  constructor(private datePipe: DatePipe, private route: ActivatedRoute, private router: Router, private renderer: Renderer2) {
    this.pinMovedEvent.subscribe(data => {
      this.locationData = {
        x: data.latitude,
        y: data.longitude
      }
    });
  }

  async initializeMap(e:any) {
    if (this._isPrimaryMap) {
      return this.initializeCrimeGunMap();
    }
    else {
      return this.initializeDragAndDropMap(e);
    }
  }

  async initializeCrimeGunMap() {
    try {

      // Configure the Map
      const map:Map = new Map({
        basemap: this._basemap,
      });

      const addressGraphics = this._createGraphics();

      // Initialize the MapView
      const mapView = new MapView({
        container: this.mapViewEl.nativeElement,
        center: this._center,
        zoom: this._zoom,
        map: map
      });
      this._mapView = mapView;

      mapView.graphics.addMany(addressGraphics);

      return mapView;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async initializeDragAndDropMap(e:any) {
    try {
      if (e.gunEvents.previousValue != undefined
        && e.gunEvents.currentValue[0].StreetAddress == e.gunEvents.previousValue[0].StreetAddress.trim()) {
        return null;
      }

      // Configure the Map
      const map: __esri.Map = new Map({
        basemap: this._basemap,
      });

      const addressGraphics = this._createDragAndDropGraphics();

      const textSymbol = {
        type: 'text',
        color: 'red',
        text: addressGraphics[0].attributes.ConsolidatedAddressReturned,
        lineWidth: 200,
        xoffset: 15,
        yoffset: 4,
        horizontalAlignment: 'left',
        font: {
          size: 12,
          weight: 'lighter'
        }
      } as __esri.TextSymbolProperties;

      const graphicsWithTextSymbol = new Graphic({
        attributes: {},
        geometry: addressGraphics[0].geometry,
        symbol: textSymbol
      });

      // Initialize the MapView
      const mapView = new MapView({
        container: this.mapViewEl.nativeElement,
        center: this._center,
        zoom: 15,
        map: map
      });

      mapView.graphics.addMany(addressGraphics);
      mapView.graphics.addMany([graphicsWithTextSymbol]);

      mapView.when().then(() =>
        mapView.on('drag', (e:any) => {
          // if dragging the graphic in question has began
          mapView.hitTest(e).then((res:any) => {

            if (res.results[0] == undefined) {
              return;
            }

            if (addressGraphics[0].geometry.spatialReference.wkid === this._wkid && e.action === "start") {
              this.isDragging = true;
            }
            else if (e.action === "end") {
              this.isDragging = false
              this.pinMovedEvent.emit(mapView.toMap(e));


              let pt = {
                type: 'point',
                latitude: this.locationData.x,
                longitude: this.locationData.y,
                spatialReference: { wkid: this._wkid },

              };

              let markerSymbol = {
                type: 'picture-marker',
                url: '/assets/images/map-pin-icon.jpg',
                width: '30px',
                height: '30px'
              } as __esri.PictureMarkerSymbolProperties;

              let pinnedAddressGraphics = [new Graphic({
                attributes: {
                  ConsolidatedAddressReturned: this.consolidatedAddress,
                },
                geometry: pt,
                symbol: markerSymbol
              })];

              const textSymbol = {
                type: 'text',
                color: 'red',
                text: pinnedAddressGraphics[0].attributes.ConsolidatedAddressReturned,
                lineWidth: 200,
                xoffset: 15,
                yoffset: 4,
                horizontalAlignment: 'left',
                font: {
                  size: 12,
                  weight: 'lighter'
                }
              };

              const graphicsWithTextSymbol = new Graphic({
                attributes: {},
                geometry: pinnedAddressGraphics[0].geometry,
                symbol: textSymbol
              });

              mapView.graphics.removeAll();
              mapView.graphics.addMany(pinnedAddressGraphics);
              mapView.graphics.addMany([graphicsWithTextSymbol]);
            }
          })

          if (this.isDragging) {
            // prevent the map being panned mid-drag
            e.stopPropagation();
            // Update the graphic's geometry to the new drag pos
            const geometry: Point = mapView.toMap(e)

            // Showing the point only while dragging
            const graphic = new Graphic({
              geometry,
              symbol: {
                type: 'picture-marker',
                url: '/assets/images/map-pin-icon.jpg',
                width: '30px',
                height: '30px'
              } as __esri.PictureMarkerSymbolProperties
            });

            mapView.graphics.removeAll();
            mapView.graphics.add(graphic);
          }
        })
      )

      return mapView;

    } catch (error) {
      console.log(error);
      return null;
    }
  }

  ngOnChanges(e: SimpleChanges) {
    for (let i = 0; i < this.gunEvents?.length; i++) {
      if (this.gunEvents[i].EventAddresses) {
        for (let j = 0; j < this.gunEvents[i].EventAddresses.length; j++) {
          let location = {
            LocationAddress: this.gunEvents[i].EventAddresses[j],
            LECaseNumber: this.gunEvents[i].LECaseNumber,
            CrimeType: this.gunEvents[i].CrimeType === 'Other' ? 'Other:' + this.gunEvents[i].OtherCrimeType : this.gunEvents[i].CrimeType,
            EventDateTime: this.gunEvents[i].EventDateTime,
            EventDateOffset: this.gunEvents[i].EventDateOffset,
            TimeOffset: this.gunEvents[i].TimeOffset,
            TimeZoneAbbreviation: this.gunEvents[i].TimeZoneAbbreviation
          };

          this._addresses.push(location);
        }
      } else {
        this._addresses.push({
          LocationAddress: this.gunEvents[i],
          LECaseNumber: null,
          CrimeType: null,
          EventDateTime: null,
          EventDateOffset: null,
          TimeOffset: null,
          TimeZoneAbbreviation: null
        });
      }
    }

    this.initializeMap(e).then((mapView: __esri.MapView) => {
      this.createLayers(mapView);
    });
  }

  public createLayers(map: __esri.MapView): void {
    if (!map) {
      return;
    }

    map.when().then((mv:__esri.MapView) => {
      // Workaround for @arcgis/core 4.29 known bug
      //https://community.esri.com/t5/arcgis-javascript-maps-sdk-questions/zoom-buttons-not-functioning-with-arcgis-core-4-29/td-p/1399172
      mv.ui.remove("zoom");
      mv.ui.add("zoom", "top-left");
      // Workaround for @arcgis/core 4.29 known bug
      //https://community.esri.com/t5/arcgis-javascript-maps-sdk-questions/map-wherever-i-click-it-opens-a-popup/td-p/1400768
      reactiveUtils.watch(
        () => mv.popup?.viewModel?.active,
        (active) => {
          if (active && !mv.popup.selectedFeature) {
            mv.closePopup();
          }
        }
      );
      // zoom to show all markers
      mv.goTo(map.graphics);

      // This prevents the map from doubling in size
      this.renderer.setStyle(this.mapViewEl.nativeElement, 'width', '100%');
      this.renderer.setStyle(this.mapViewEl.nativeElement, 'height', '100%');

      this.mapLoadedEvent.emit(true);
    });
  }

  private getLatLongPoint(address: any): any {
    let p = null;
    if (address.LocationAddress) {
      if (address.LocationAddress.EnteredLatitude && address.LocationAddress.EnteredLatitude != 0) {
        p = { lat: address.LocationAddress.EnteredLatitude, long: address.LocationAddress.EnteredLongitude };
      } else if (address.LocationAddress.CapturedLatitude && address.LocationAddress.CapturedLatitude != 0) {
        p = { lat: address.LocationAddress.CapturedLatitude, long: address.LocationAddress.CapturedLongitude };
      }
    }
    return p;
  }

  private _createGraphics(): any[] {
    if (!this._addresses.length) {
      return null;
    }

    let addressGraphics = this._addresses.map(function (address) {
      let llp = this.getLatLongPoint(address);
      if (!llp) {
        return null;
      }

      let pt = {
        type: 'point',
        latitude: parseFloat(llp.lat),
        longitude: parseFloat(llp.long),
        spatialReference: { wkid: 4326 }
      };

      let markerSymbol = {
        type: 'simple-marker',
        style: 'circle',
        color: [197, 49, 28],
        size: '10px',
        outline: {
          color: [134, 33, 19],
          width: 1
        }
      };
      let popupTmpl = {
        title: 'LE case # {LECaseNumber}',
        content: '<div><strong>Event Date:</strong> {EventDateTime}</div><div><strong>Crime type:</strong> {CrimeType}</div>'
      };
      return {
        attributes: {
          AddressID: address.LocationAddress.AddressID,
          ConsolidatedAddressReturned: AppUtils.formatEventAddress(address.LocationAddress),
          LECaseNumber: address.LECaseNumber,
          CrimeType: address.CrimeType,
          EventDateTime: this.datePipe.transform(address.EventDateOffset, 'MM/dd/yyyy  HH:mm', address.TimeOffset) + " " + address.TimeZoneAbbreviation

        },
        geometry: this.LatAndLogClearInValidData(pt),
        symbol: markerSymbol,
        popupTemplate: popupTmpl
      };
    }, this);

    return addressGraphics;
  }

  private _createDragAndDropGraphics(): any[] {
    if (!this._addresses.length) {
      return null;
    }

    let addressGraphics = this._addresses.map(function (address) {

      let llp = this.getLatLongPoint(address);
      if (!llp) {
        return null;
      }

      let pt = {
        type: 'point',
        latitude: parseFloat(llp.lat),
        longitude: parseFloat(llp.long),
        spatialReference: { wkid: 4326 },

      };
      let markerSymbol = {
        type: 'picture-marker',
        url: '/assets/images/map-pin-icon.jpg',
        width: '30px',
        height: '30px'
      };

      return {
        attributes: {
          AddressID: address.LocationAddress.AddressID,
          ConsolidatedAddressReturned: address.LocationAddress.ConsolidatedAddressReturned.split(',').map(function (item:any, idx:number) { if (idx == 1) { return '\n' + item.trim(); } else { return item; } }).toString(),
          LECaseNumber: address.LECaseNumber,
          CrimeType: address.CrimeType,
          EventDateTime: this.datePipe.transform(address.EventDateOffset, 'MM/dd/yyyy  HH:mm', address.TimeOffset) + " " + address.TimeZoneAbbreviation

        },
        geometry: this.LatAndLogClearInValidData(pt),
        symbol: markerSymbol
      };
    }, this);

    this.consolidatedAddress = addressGraphics[0].attributes.ConsolidatedAddressReturned;
    return addressGraphics;
  }

  private LatAndLogClearInValidData(pt: any): any {
    if (!!pt) {
      const isLongValid = this.longitudeRegEx.test(pt.longitude);
      if (!isLongValid) {
        pt.longitude = 0;
      }
      const isLatValid = this.latitudeRegEx.test(pt.latitude);
      if (!isLatValid) {
        pt.latitude = 0;
      }
    }
    return pt;
  }

  // 2:1 aspect ratio determine inline in the HTML file.  Keep these values in sync with HTML
  public printMap() : any {
    let options = {
      format: "jpg",
      quality: "80",
      width: 1250,
      height: 625
    }
    this._mapView.takeScreenshot(options).then(function (screenshot) {
      console.log(screenshot.dataUrl)
      //navigator.clipboard.writeText(screenshot.dataUrl);
      //window.open(screenshot.dataUrl.toString(),'_blank')
    });
  }

}
